import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useAppDispatch, useAppSelector } from '../../store/hooks'
import { selectAccountId } from '../../store/auth/authSlice'
import {
  useDeleteUserShareSettingsMutation,
  useGetUserByIdQuery,
  useSaveUserByIdMutation,
  useSaveUserShareSettingsMutation,
  useUpdateUserRolesMutation,
} from '../../store/user/user'
import { Controller, FormProvider, useForm, useFormContext } from 'react-hook-form'
import { Col, Container, Form, Row, Spinner } from 'react-bootstrap'
import { Modal } from '../../components/Modal/Modal'
import { FormFeedback } from '../../components/Form/FormFeedback'
import { getUserName } from '../utils'
import { getInviteRoleGroups, getRoleGroupForRoles } from './InviteRoleGroups'
import { SelectDropdown, SelectOption } from '../../components/SelectDropdown/SelectDropdown'
import { useGetRolesQuery } from '../../store/role/role'
import { MultiSelectDropdown } from '../../components/MultiSelectDropdown/MultiSelectDropdown'
import { getRoleDisplayName } from '../../components/Roles/utils'
import { Role } from '../../models/Role'
import { getUserDisplayName } from '../../apps/professional-app/fa/common/aifeatures/utils'
import { addToast } from '../../store/toast/toastSlice'
import { UserShareForm } from '../ProfileSettings/CollaborationTab'
import { DEFAULT_SESSION_SHARING_ENABLED, useGetAccountUserSettingsQuery } from '../../store/accountUserSettings/accountUserSettings'

export interface EditUserModalProps {
  userId: string
  show: boolean
  onHide: () => void
  onUserEdited?: () => void
}

type FormValues = {
  firstName: string
  lastName: string
  email: string
  phoneNumber: string
  roleIds: string[]
}

export const EditUserModal: React.FC<EditUserModalProps> = ({ userId, show, onHide, onUserEdited }) => {
  const accountId = useAppSelector(selectAccountId)
  const dispatch = useAppDispatch()

  const {
    data: userData,
    isLoading,
    isFetching: userDataFetching,
  } = useGetUserByIdQuery({ accountId, userId }, { skip: !show, refetchOnMountOrArgChange: true })
  const isDeactivated = userData?.data.status === 'deactivated'

  // --- Sharing state (deactivated path) ---
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([])
  const [saveUserShareSettings] = useSaveUserShareSettingsMutation()
  const [deleteUserShareSettings] = useDeleteUserShareSettingsMutation()
  const [saveUserByIdForSharing] = useSaveUserByIdMutation()
  const [isSaving, setIsSaving] = useState(false)

  const { data: accountUserSettings, isFetching: accountUserSettingsLoading } = useGetAccountUserSettingsQuery({ accountId }, { skip: !show })

  const [sharingEnabled, setSharingEnabled] = useState<boolean>(DEFAULT_SESSION_SHARING_ENABLED)

  useEffect(() => {
    if (!userData || !accountUserSettings) {
      return
    }
    const userSetting = userData.data.settings
    const isUserSharingEnabled = !!(userSetting && userSetting.contactSharingEnabled && userSetting.sessionSharingEnabled)
    const isAccountSharingEnabled = !!(
      accountUserSettings.data &&
      accountUserSettings.data.settingsOverride &&
      accountUserSettings.data.settingsOverride.sessionSharingEnabled &&
      accountUserSettings.data.settingsOverride.contactSharingEnabled
    )
    if (userSetting) {
      setSharingEnabled(isUserSharingEnabled)
    } else if (accountUserSettings.data?.settingsOverride) {
      setSharingEnabled(isAccountSharingEnabled)
    } else {
      setSharingEnabled(DEFAULT_SESSION_SHARING_ENABLED)
    }
  }, [userData, accountUserSettings])

  const handleSharingEnabledChanged = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSharingEnabled(e.currentTarget.checked)
  }

  const handleShareSave = useCallback(async () => {
    if (!userData) {
      return
    }
    try {
      setIsSaving(true)
      const currentSettings = userData.data.settings
      await saveUserByIdForSharing({
        accountId,
        userId,
        userData: { settings: { ...currentSettings, sessionSharingEnabled: sharingEnabled, contactSharingEnabled: sharingEnabled } },
      })
      if (sharingEnabled) {
        await saveUserShareSettings({
          accountId,
          userId,
          payload: { shareType: 'meeting', sharedWithUserIds: selectedUserIds },
        })
        await saveUserShareSettings({
          accountId,
          userId,
          payload: { shareType: 'contact', sharedWithUserIds: selectedUserIds },
        })
      } else {
        await deleteUserShareSettings({ accountId, userId, shareType: 'meeting' })
        await deleteUserShareSettings({ accountId, userId, shareType: 'contact' })
      }
      dispatch(addToast({ variant: 'success', title: 'Sharing updated', messages: ['Sharing settings have been saved.'] }))
      setIsSaving(false)
      onUserEdited?.()
      onHide()
    } catch {
      dispatch(addToast({ variant: 'error', title: 'Save failed', messages: ['Failed to save sharing settings. Please try again.'] }))
      setIsSaving(false)
    }
  }, [
    userData,
    onHide,
    onUserEdited,
    saveUserShareSettings,
    deleteUserShareSettings,
    saveUserByIdForSharing,
    accountId,
    dispatch,
    selectedUserIds,
    userId,
    sharingEnabled,
  ])

  // --- Edit form state (active path) ---
  const [saveUserById, { isLoading: isUpdating, isSuccess }] = useSaveUserByIdMutation()
  const [updateUserRoles, { isLoading: isRolesUpdating, isSuccess: updateRolesSuccess }] = useUpdateUserRolesMutation()

  const form = useForm<FormValues>({
    defaultValues: { firstName: '', lastName: '', email: '', phoneNumber: '', roleIds: [] },
  })
  const { handleSubmit } = form

  const handleEditSave = (data: FormValues) => {
    saveUserById({
      accountId,
      userId,
      userData: {
        firstName: data.firstName,
        lastName: data.lastName,
        name: getUserName(data.firstName, data.lastName) || undefined,
        email: data.email,
      },
    })
    updateUserRoles({
      accountId,
      userId,
      roleIds: data.roleIds,
    })
  }

  useEffect(() => {
    if (!isDeactivated && isSuccess && updateRolesSuccess) {
      onUserEdited?.()
      onHide()
    }
  }, [isSuccess, isDeactivated, updateRolesSuccess, onUserEdited, onHide])

  // --- Loading state ---
  if (!userData || isLoading || (isDeactivated && userDataFetching)) {
    return (
      <Modal
        show={show}
        onHide={onHide}
        header={<></>}
        body={
          <div className='d-flex align-items-center justify-content-center p-4'>
            <Spinner />
          </div>
        }
        showFooterActions={false}
        centered={true}
      />
    )
  }

  // --- Conditional body content ---
  const displayName = getUserDisplayName(userData.data)

  const sharingBody = (
    <div className='d-flex flex-column gap-4'>
      <p className='paragraph-medium mb-0'>
        Give another team members access to {displayName}'s <strong>past meetings and contacts</strong>. They can view and edit this data, but they don't gain
        ownership.
      </p>
      <div className='d-flex flex-column gap-3'>
        <div className='d-flex justify-content-between'>
          <span className='paragraph-medium'>Share past meetings and contacts with team members</span>
          <Form.Switch disabled={accountUserSettingsLoading} checked={sharingEnabled} onChange={handleSharingEnabledChanged} />
        </div>
        <div>
          {sharingEnabled && (
            <UserShareForm
              shareType='meeting'
              attachedShareTypes={['contact']}
              noSelectedUserText='Share access with all team members'
              multiSelectWarningText='If no specific users are selected data will be shared with all team members.'
              hideSaveButton
              onSelectedUsersChange={setSelectedUserIds}
              userId={userId}
              showInModal
            />
          )}
        </div>
      </div>
    </div>
  )

  const editBody = (
    <Container>
      <Row className='pb-3'>
        <Col>
          <h4 className='d-flex justify-content-center align-items-center'>Edit User</h4>
        </Col>
      </Row>
      <Row>
        <FormProvider {...form}>
          <EditUserModalForm userId={userId} show={show} onHide={onHide} />
        </FormProvider>
      </Row>
    </Container>
  )

  return (
    <Modal
      show={show}
      onHide={onHide}
      header={isDeactivated ? <h4>Manage sharing</h4> : <></>}
      headerBodySeparator={isDeactivated}
      body={isDeactivated ? sharingBody : editBody}
      showFooterActions={true}
      onFooterPrimaryAction={isDeactivated ? handleShareSave : handleSubmit(handleEditSave)}
      footerPrimaryActionLoading={isDeactivated ? isSaving : isUpdating || isRolesUpdating}
      footerPrimaryActionDisabled={isDeactivated ? isSaving || accountUserSettingsLoading : undefined}
      footerPrimaryActionTitle={isDeactivated ? 'Save' : undefined}
      footerPrimaryActionButtonVariant={isDeactivated ? 'primary' : undefined}
      footerSecondaryActionDisabled={isDeactivated ? isSaving : undefined}
      centered
    />
  )
}

type RoleDisplay = Role & { disabled: boolean }

const EditUserModalForm: React.FC<EditUserModalProps> = ({ userId }) => {
  const { register, control, setValue } = useFormContext<FormValues>()

  const accountId = useAppSelector(selectAccountId)
  const roleGroups = useMemo(() => getInviteRoleGroups(), [])

  const { data: userData, isFetching: isFetchingUserData } = useGetUserByIdQuery({ accountId: accountId, userId: userId })

  const { data: roleData, isFetching: isFetchingRoleData } = useGetRolesQuery(
    {
      accountId: accountId,
      pageRequest: { page: 0, size: 500 },
    },
    { refetchOnMountOrArgChange: true },
  )

  const [areUserRolesInGroup, setAreUserRolesInGroup] = useState<boolean>(false)

  useEffect(() => {
    if (userData) {
      setValue('firstName', userData.data.firstName ?? '')
      setValue('lastName', userData.data.lastName ?? '')
      setValue('email', userData.data.email ?? '')

      const userRoleGroupName = getRoleGroupForRoles(userData.data.roles.map((role) => role.id))
      if (userRoleGroupName) {
        setAreUserRolesInGroup(true)
      }
      setValue(
        'roleIds',
        userData.data.roles.map((role) => role.id),
      )
    }
  }, [userData])

  const [roles, setRoles] = useState<RoleDisplay[]>([])

  useEffect(() => {
    if (roleData && userData) {
      const roleIds = roleGroups.map((rg) => rg.roleIds).flat()
      const userRoles = userData.data.roles.map((ur) => ur.id)
      const roles = roleData.data
        .filter((role) => {
          return roleIds.includes(role.id) || userRoles.includes(role.id)
        })
        .map((role) => {
          const roleGroup = getRoleGroupForRoles([role.id])
          return {
            ...role,
            name: roleGroup?.name || role.name,
            disabled: !roleIds.includes(role.id) && userRoles.includes(role.id),
          }
        })
      setRoles(roles)
    }
  }, [roleData, userData])

  const handleRoleGroupSelected = (option: SelectOption) => {
    const roleGroup = roleGroups.find((e) => e.name === option.id)
    setValue('roleIds', roleGroup?.roleIds ?? [])
  }

  return (
    <Container className='d-flex flex-column gap-4 p-0'>
      {isFetchingUserData || isFetchingRoleData ? (
        <div className='d-flex align-items-center justify-content-center'>
          <Spinner />
        </div>
      ) : (
        <>
          <Row>
            <Col>
              <Controller
                name='email'
                control={control}
                rules={{
                  required: 'Email address is required.',
                  pattern: {
                    value: /\S+@\S+\.\S+/,
                    message: 'Entered value does not match email format',
                  },
                }}
                render={({ field, fieldState }) => (
                  <>
                    <Form.Label>Email address*</Form.Label>
                    <Form.Control id={field.name} type='email' disabled={true} placeholder='Enter email address…' {...field} isInvalid={!!fieldState.error} />
                    {fieldState.error && <FormFeedback>{fieldState.error.message}</FormFeedback>}
                  </>
                )}
              />
            </Col>
          </Row>
          <Row>
            <Col>
              <Controller
                name='firstName'
                control={control}
                render={({ field, fieldState }) => (
                  <>
                    <Form.Label>First name</Form.Label>
                    <Form.Control id={field.name} type='text' placeholder='Enter first name…' {...field} />
                  </>
                )}
              />
            </Col>
          </Row>
          <Row>
            <Col>
              <Controller
                name='lastName'
                control={control}
                render={({ field, fieldState }) => (
                  <>
                    <Form.Label>Last name</Form.Label>
                    <Form.Control id={field.name} type='text' placeholder='Enter last name…' {...field} />
                  </>
                )}
              />
            </Col>
          </Row>
          {/*<Row>*/}
          {/*  <Col>*/}
          {/*    <Controller*/}
          {/*      name='phoneNumber'*/}
          {/*      control={control}*/}
          {/*      rules={{*/}
          {/*        pattern: { value: /^\+[1-9][0-9]{0,24}$/i, message: 'Invalid phone number. E.g. +11234567890' },*/}
          {/*      }}*/}
          {/*      render={({ field, fieldState }) => (*/}
          {/*        <>*/}
          {/*          <Form.Label>Phone number</Form.Label>*/}
          {/*          <Form.Control id={field.name} type='text' placeholder='Enter phone number…' {...field} />*/}
          {/*        </>*/}
          {/*      )}*/}
          {/*    />*/}
          {/*  </Col>*/}
          {/*</Row>*/}
          <Row>
            <Col>
              <Controller
                name='roleIds'
                control={control}
                render={({ field, fieldState }) => (
                  <>
                    <Form.Label>Roles</Form.Label>
                    {areUserRolesInGroup ? (
                      <SelectDropdown
                        className='zocks-px-0.75 py-2'
                        options={roleGroups.map((roleGroup) => {
                          return { id: roleGroup.name, label: roleGroup.name }
                        })}
                        initialSelectedOption={roleGroups
                          .map((roleGroup) => {
                            return { id: roleGroup.name, label: roleGroup.name }
                          })
                          .find((option) => option.id === getRoleGroupForRoles(field.value)?.name)}
                        onOptionSelected={handleRoleGroupSelected}
                      />
                    ) : (
                      <MultiSelectDropdown
                        register={register}
                        name='roleIds'
                        options={
                          roles.map((role) => {
                            return { id: role.id, label: getRoleDisplayName(role), disabled: role.disabled }
                          }) || []
                        }
                        initialSelectedOptions={field.value}
                      />
                    )}
                  </>
                )}
              />
            </Col>
          </Row>
        </>
      )}
    </Container>
  )
}
